import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { pluginKey } from '../../common/agentManagedExtensionPluginType';
import { findAgentManagedExtension } from '../../common/utils';
import { setNodeViewSelectedByLocalId } from '../nodeviews/agentManagedExtensionNodeView';

const getSelectedAgentManagedLocalId = (editorView: EditorView): string | undefined =>
	findAgentManagedExtension(editorView.state)?.node.attrs.localId as string | undefined;

const getOpenUiLocalId = (editorView: EditorView): string | undefined => {
	const pluginState = pluginKey.getState(editorView.state);

	if (pluginState?.isConfigOpen) {
		return pluginState.configLocalId ?? getSelectedAgentManagedLocalId(editorView);
	}

	if (pluginState?.isAutoUpdateIntervalOpen) {
		return pluginState.autoUpdateIntervalLocalId;
	}

	return undefined;
};

/**
 * While agent-managed block UI is open, ensure the block appears visually
 * selected (border, lozenge) even though focus has moved to the popup/dropdown.
 *
 * Instead of dispatching a `NodeSelection` transaction (which causes
 * ProseMirror to scroll the document), we directly mark the node view
 * as selected and re-render it.
 *
 * Returns the `view` object expected by the ProseMirror plugin spec.
 */
export const createSelectedWhileConfigOpenView = (editorView: EditorView) => {
	let selectedLocalId: string | undefined;

	return {
		update(): void {
			const nextSelectedLocalId = getOpenUiLocalId(editorView);

			if (nextSelectedLocalId === selectedLocalId) {
				return;
			}

			if (selectedLocalId) {
				setNodeViewSelectedByLocalId(selectedLocalId, false);
			}

			selectedLocalId = nextSelectedLocalId;

			if (selectedLocalId) {
				setNodeViewSelectedByLocalId(selectedLocalId, true);
			}
		},
	};
};
